import type { ReactNode } from 'react';
import { StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { NativeSheetScrollView } from '../common/NativeSheetLayout';
import { Text } from '@/components/common/AppText';
import { NovaMood, type NovaMoodName } from './NovaMood';
import { JourneyFooter } from './JourneyFooter';
import { TEXT_START, useJourneyPalette } from './journey-theme';

/**
 * One journey step inside a native Stack screen: scrolling body (22pt gutters,
 * clears the transparent iOS header automatically) and an optional sticky
 * footer. The scroller is the first native child so the sheet can find it.
 */
export function JourneyScreen({
  children,
  footer,
  contentStyle,
  keyboard = false,
}: {
  children: ReactNode;
  footer?: ReactNode;
  contentStyle?: StyleProp<ViewStyle>;
  /** Screens with a note field: keep the focused input above the keyboard. */
  keyboard?: boolean;
}) {
  const palette = useJourneyPalette();
  if (keyboard) {
    return (
      <View style={[styles.root, { backgroundColor: palette.bg }]}>
        <KeyboardAwareScrollView
          bottomOffset={24}
          contentInsetAdjustmentBehavior="automatic"
          keyboardShouldPersistTaps="handled"
          style={[styles.root, { backgroundColor: palette.bg }]}
          contentContainerStyle={[styles.content, contentStyle]}
          showsVerticalScrollIndicator={false}
        >
          {children}
        </KeyboardAwareScrollView>
        {footer}
      </View>
    );
  }
  return (
    <View style={[styles.root, { backgroundColor: palette.bg }]}>
      <NativeSheetScrollView
        style={{ backgroundColor: palette.bg }}
        contentContainerStyle={[styles.content, contentStyle]}
        showsVerticalScrollIndicator={false}
      >
        {children}
      </NativeSheetScrollView>
      {footer}
    </View>
  );
}

export type JourneyLayoutMode = 'screen' | 'inline';

/**
 * `screen`: own scroller + sticky footer (route hosts). `inline`: body then
 * footer stacked, for hosts that already scroll (e.g. the preserved
 * onboarding paywall step).
 */
export function JourneyLayout({
  mode,
  body,
  footer,
  contentTopPadding,
}: {
  mode: JourneyLayoutMode;
  body: ReactNode;
  footer: ReactNode;
  /** `screen` only: top padding of the scroll content (a host's own chrome overlay). */
  contentTopPadding?: number;
}) {
  if (mode === 'screen') {
    return (
      <JourneyScreen
        footer={<JourneyFooter>{footer}</JourneyFooter>}
        contentStyle={contentTopPadding ? { paddingTop: contentTopPadding } : undefined}
      >
        {body}
      </JourneyScreen>
    );
  }
  return (
    <View style={styles.inline}>
      {body}
      <View style={styles.inlineFooter}>{footer}</View>
    </View>
  );
}

/** Screen heading + optional subtitle (`.screen-heading` / `.screen-subtitle`). */
export function JourneyHeading({
  title,
  subtitle,
  center = false,
  size = 29,
}: {
  title: string;
  subtitle?: string | null;
  center?: boolean;
  size?: number;
}) {
  const palette = useJourneyPalette();
  const align = center ? ({ textAlign: 'center' } as const) : TEXT_START;
  return (
    <View style={styles.heading}>
      <Text
        accessibilityRole="header"
        style={[
          styles.title,
          { color: palette.text, fontSize: size, lineHeight: Math.round(size * 1.18) },
          align,
        ]}
      >
        {title}
      </Text>
      {subtitle ? (
        <Text style={[styles.subtitle, { color: palette.secondary }, align]}>{subtitle}</Text>
      ) : null}
    </View>
  );
}

/** A decorative Nova "moment" above a heading (`.compact-moment`). */
export function JourneyMoment({
  mood,
  size = 115,
  flat,
  align = 'center',
}: {
  mood: NovaMoodName;
  size?: number;
  flat?: boolean;
  align?: 'center' | 'start';
}) {
  return (
    <View style={[styles.moment, align === 'start' && styles.momentStart]}>
      <NovaMood mood={mood} size={size} flat={flat} />
    </View>
  );
}

/** Section title (`.section-title`). */
export function JourneySectionTitle({ title }: { title: string }) {
  const palette = useJourneyPalette();
  return (
    <Text accessibilityRole="header" style={[styles.section, { color: palette.text }, TEXT_START]}>
      {title}
    </Text>
  );
}

/** Small print (`.fine`). */
export function JourneyFine({ children, center }: { children: ReactNode; center?: boolean }) {
  const palette = useJourneyPalette();
  return (
    <Text
      style={[
        styles.fine,
        { color: palette.secondary },
        center ? { textAlign: 'center' } : TEXT_START,
      ]}
    >
      {children}
    </Text>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1 },
  inline: { paddingHorizontal: 22 },
  inlineFooter: { gap: 8, marginTop: 20 },
  content: { paddingHorizontal: 22, paddingTop: 16, paddingBottom: 24 },
  heading: { gap: 7 },
  title: { fontWeight: '700', letterSpacing: -0.6 },
  subtitle: { fontSize: 15, lineHeight: 22 },
  moment: { alignItems: 'center', marginTop: 6, marginBottom: 24 },
  momentStart: { alignItems: 'flex-start' },
  section: { fontSize: 15, fontWeight: '600', marginTop: 24, marginBottom: 10 },
  fine: { fontSize: 12, lineHeight: 17, marginTop: 12 },
});
